/**
 * Edit-&-resend destructive tail (R1.2). Shared by every edit path in both chat
 * surfaces (HeroChat + ChatPanel): the ordinary edit, the file-changes choice
 * dialog, and the restore-versions modal.
 *
 * The flow truncates the conversation INCLUSIVELY (removes the edited message and
 * everything after it) and THEN sends the edited text. The truncation is
 * destructive and irreversible, and it runs BEFORE the send. If the send then
 * fails — the session became busy again after sendMessage's bounded 409 retries,
 * so it returns `false` — the old code cleared attachments and closed edit mode
 * regardless. Result: the original message was already deleted AND the edited draft
 * was thrown away, so the user lost both.
 *
 * This helper routes success and failure to DISTINCT callbacks so a caller can only
 * clear edit state on success, and on failure RETAIN the edited draft (re-seed the
 * composer) so nothing is lost and the user can resend once the session frees.
 *
 * A fully-atomic server operation (truncate + enqueue in one transaction, so the
 * truncation only commits if the enqueue's busy-check passes) would be a stronger
 * guarantee, but the client sends over the AG-UI run route and that refactor is out
 * of scope here; draft retention makes the failure fully recoverable in the meantime.
 *
 * `restore` and `send` are pre-bound by the caller (they close over messageId /
 * editedText / model / attachments / context), so this stays a pure, dependency-free
 * orchestrator — unit-testable without React.
 *
 * Returns the send result.
 */
export async function truncateAndResend(p: {
  /** Pre-bound restoreConversation(messageId, { inclusive: true }). */
  restore: () => Promise<unknown>
  /** Pre-bound sendMessage(editedText, editAttachments, …); resolves false on 409-exhausted / abort. */
  send: () => Promise<boolean>
  /** Send succeeded: safe to clear attachments + close edit mode. */
  onSent: () => void
  /** Send FAILED (or restore/send REJECTED) after the truncation may already have
   *  committed: RETAIN the edited draft (never lose it). */
  onFailed: () => void
}): Promise<boolean> {
  // R2.3: route EVERY non-success through onFailed, including a rejection. A
  // rejected restore() is the dangerous case — the server may have already
  // committed the truncation (its RESPONSE was lost, not the write), so the
  // original message is gone; if we let the throw propagate, the edited draft is
  // never preserved. A rejected send() (unexpected error) is the same class. So
  // the ONLY path that clears edit state is a resolved, truthy send.
  try {
    await p.restore()
    const ok = await p.send()
    if (ok) { p.onSent(); return true }
    p.onFailed()
    return false
  } catch {
    p.onFailed()
    return false
  }
}

/**
 * R2.3: preserve BOTH the pre-existing composer draft and the failed edit rather
 * than choosing one with `||`. Starting a message edit does not clear the main
 * composer, so `prev` can already hold an unsent draft; `prev || edited` would then
 * silently discard the edit (whose original message was just truncated). Merge them
 * with a blank-line separator so nothing is lost. Identical text isn't duplicated.
 */
export function mergeDraft(existing: string, edited: string): string {
  if (!existing.trim()) return edited
  if (existing === edited) return existing
  return `${existing}\n\n${edited}`
}
